import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import { useTenant } from './context/TenantContext';

const STATUS_LABELS = {
  pending:     'Pendiente',
  delivered:   'Entregado',
  at_boxful:   'En Boxful',
  rescheduled: 'Reprogramado',
};

const STATUS_COLORS = {
  pending:     { bg: '#f3f4f6', color: '#6b7280',  border: '#d1d5db' },
  delivered:   { bg: '#dcfce7', color: '#15803d',  border: '#86efac' },
  at_boxful:   { bg: '#dbeafe', color: '#1d4ed8',  border: '#93c5fd' },
  rescheduled: { bg: '#fff7ed', color: '#c2410c',  border: '#fdba74' },
};

// ── Box numbers ───────────────────────────────────────────────────────────────
// display_sequence and global_package_id come from the database; the code is
// only ever read here, never built.
const BOX_ROW_COLS = 'box_id, display_sequence, global_package_id, manifest_id';

const SEQ_TAKEN_MSG = 'Ese número ya fue tomado. Actualiza la lista e intenta de nuevo.';

const fetchBoxRows = (tenantId, boxIds) =>
  supabase.from('boxes').select(BOX_ROW_COLS).eq('tenant_id', tenantId).in('box_id', boxIds);

const boxRowsToMaps = (rows) => {
  const seqs = {};
  const info = {};
  for (const b of (rows || [])) {
    seqs[b.box_id] = b.display_sequence;
    info[b.box_id] = { manifest_id: b.manifest_id, global_package_id: b.global_package_id };
  }
  return { seqs, info };
};

const GuiasPortal = ({ user }) => {
  const { tenant } = useTenant();
  const currentTenantId = tenant?.id;
  const currentUserId   = user?.id;
  const isDispatch  = user?.role === 'dispatch';
  const isManager   = user?.role === 'admin' || user?.role === 'manager';
  const canEditSeq  = ['admin', 'manager', 'bodega'].includes(user?.role);

  const [guias,           setGuias]           = useState([]);
  const [selectedGuiaId,  setSelectedGuiaId]  = useState(null);
  const [stops,           setStops]           = useState([]);
  const [boxPackagesData, setBoxPackagesData] = useState({});
  const [customerNames,   setCustomerNames]   = useState({});
  const [boxClientGroups, setBoxClientGroups] = useState({});  // { "boxId|clientId": weight_lb }
  const [customerAddresses, setCustomerAddresses] = useState({});  // { clientId: addrObj }
  const [drivers,         setDrivers]         = useState([]);
  const [selectedDriverId,setSelectedDriverId]= useState('');
  const [savingDriver,    setSavingDriver]    = useState(false);
  const [loading,         setLoading]         = useState(true);
  const [stopsLoading,    setStopsLoading]    = useState(false);
  const [updatingStopId,  setUpdatingStopId]  = useState(null);
  const [boxSequences,    setBoxSequences]    = useState({});
  const [boxInfo,         setBoxInfo]         = useState({});  // { boxId: { manifest_id, global_package_id } }
  const [editingSeqBoxId, setEditingSeqBoxId] = useState(null);
  const [seqMsg,          setSeqMsg]          = useState(null); // { tipo: 'ok' | 'error', texto }
  const seqSavingRef = useRef(false);
  const channelRef = useRef(null);
  const [expandedSubsections, setExpandedSubsections] = useState({});

  const selectedGuia = guias.find(g => g.id === selectedGuiaId);

  // Merge returned box rows into state so both number and code re-render from the DB.
  const applyBoxRows = (rows) => {
    const { seqs, info } = boxRowsToMaps(rows);
    setBoxSequences(prev => ({ ...prev, ...seqs }));
    setBoxInfo(prev => ({ ...prev, ...info }));
  };

  const refreshBoxRows = async () => {
    const boxIds = Object.keys(boxSequences);
    if (boxIds.length === 0) return;
    const { data, error } = await fetchBoxRows(currentTenantId, boxIds);
    if (error) {
      console.error('[GuiasPortal] refresh boxes failed:', error);
      return;
    }
    applyBoxRows(data);
  };

  // Renumber one box. If another box in the same manifest already holds the
  // number, offer a swap through swap_box_sequence instead of colliding with
  // boxes_manifest_sequence_uniq. Every failure path shows a message.
  const saveBoxSequence = async (boxId, newVal) => {
    if (seqSavingRef.current) return; // the confirm dialog blurs the input and re-fires onBlur
    const parsed = parseInt(newVal, 10);
    if (isNaN(parsed) || parsed < 1 || parsed === boxSequences[boxId]) {
      setEditingSeqBoxId(null);
      return;
    }

    seqSavingRef.current = true;
    setSeqMsg(null);
    try {
      const manifestId = boxInfo[boxId]?.manifest_id;
      if (!manifestId) {
        setSeqMsg({ tipo: 'error', texto: 'Esta caja no tiene manifiesto; no se puede renumerar.' });
        return;
      }

      const { data: holder, error: lookupErr } = await supabase
        .from('boxes')
        .select('box_id, display_sequence')
        .eq('tenant_id', currentTenantId)
        .eq('manifest_id', manifestId)
        .eq('display_sequence', parsed)
        .neq('box_id', boxId)
        .maybeSingle();
      if (lookupErr) {
        console.error('[GuiasPortal] sequence lookup failed:', lookupErr);
        setSeqMsg({ tipo: 'error', texto: 'No se pudo verificar el número. Intenta de nuevo.' });
        return;
      }

      if (holder) {
        const ok = window.confirm(`La caja ${parsed} ya tiene ese número. ¿Intercambiar los números de ambas cajas?`);
        if (!ok) return;
        const { data: swapped, error: swapErr } = await supabase.rpc('swap_box_sequence', {
          p_box_a: boxId,
          p_box_b: holder.box_id,
        });
        if (swapErr) {
          console.error('[GuiasPortal] swap_box_sequence failed:', swapErr);
          setSeqMsg({ tipo: 'error', texto: swapErr.message || 'No se pudo intercambiar los números.' });
          return;
        }
        applyBoxRows(swapped);
        setSeqMsg({ tipo: 'ok', texto: `Números intercambiados: caja ${parsed} ↔ caja ${boxSequences[boxId]}.` });
        return;
      }

      // Number looked free — a concurrent save can still take it before this write.
      const { data: updated, error: updErr } = await supabase
        .from('boxes')
        .update({ display_sequence: parsed })
        .eq('tenant_id', currentTenantId)
        .eq('box_id', boxId)
        .select(BOX_ROW_COLS)
        .single();
      if (updErr) {
        console.error('[GuiasPortal] renumber failed:', updErr);
        if (updErr.code === '23505') {
          await refreshBoxRows();
          setSeqMsg({ tipo: 'error', texto: SEQ_TAKEN_MSG });
        } else {
          setSeqMsg({ tipo: 'error', texto: 'No se pudo cambiar el número de la caja. Intenta de nuevo.' });
        }
        return;
      }
      applyBoxRows([updated]);
      setSeqMsg({ tipo: 'ok', texto: `Caja renumerada a ${updated.display_sequence}.` });
    } finally {
      seqSavingRef.current = false;
      setEditingSeqBoxId(null);
    }
  };

  // ── Fetch recent guías (unified query) ────────────────────────────────────
  useEffect(() => {
    if (!currentTenantId) return;
    const fetchGuias = async () => {
      setLoading(true);

      const { data: recentGuias, error } = await supabase
        .rpc('get_dashboard_guias', { p_tenant_id: currentTenantId })
        .select('id, manifest_id, status, created_at, driver_id');

      if (error) {
        console.error("Error fetching recent guias:", error);
      }

      setGuias(recentGuias || []);
      setSelectedGuiaId(recentGuias?.[0]?.id || null);
      setLoading(false);
    };
    fetchGuias();
  }, [currentTenantId]);

  // ── Fetch dispatch drivers (manager only) ───────────────────────────────────
  useEffect(() => {
    if (!currentTenantId || !isManager) return;
    const fetchDrivers = async () => {
      const { data } = await supabase
        .from('staff_members')
        .select('id, username')
        .eq('tenant_id', currentTenantId)
        .eq('role', 'dispatch');
      setDrivers(data || []);
    };
    fetchDrivers();
  }, [currentTenantId, isManager]);

  // ── Sync driver dropdown when selected guía changes ─────────────────────────
  useEffect(() => {
    setSelectedDriverId(selectedGuia?.driver_id || '');
  }, [selectedGuia]);

  // ── Fetch stops + box data + box_packages ───────────────────────────────────
  useEffect(() => {
    if (!selectedGuiaId || !currentTenantId) {
      setStops([]);
      setBoxPackagesData({});
      setCustomerNames({});
      setBoxClientGroups({});
      setCustomerAddresses({});
      setBoxSequences({});
      return;
    }

    const fetchStops = async () => {
      setStopsLoading(true);
      const { data: stopsData } = await supabase
        .from('guia_stops')
        .select(`
          *,
          boxes (
            box_id, customer_id,
            recipient_name, recipient_phone, recipient_address,
            municipio, departamento, reference_point, delivery_type
          )
        `)
        .eq('guia_id', selectedGuiaId)
        .eq('tenant_id', currentTenantId)
        .order('box_id', { ascending: true })
        .order('created_at', { ascending: true });

      const stopsArr = stopsData || [];
      setStops(stopsArr);

      const boxIds = stopsArr.map(s => s.box_id).filter(Boolean);
      if (boxIds.length > 0) {
        // Query 1: flat box_packages (no nested join)
        const { data: bpData, error: bpErr } = await supabase
          .from('box_packages')
          .select('box_id, client_id, package_ref_id, is_primary, package_type')
          .eq('tenant_id', currentTenantId)
          .in('box_id', boxIds);

        if (bpErr) {
          console.error('[GuiasPortal] box_packages error:', bpErr);
        }

        // Query 2: shipments lookup for casillero packages
        const casilleroRefIds = (bpData || [])
          .filter(r => r.package_type === 'casillero')
          .map(r => r.package_ref_id);

        let shipmentsMap = {};
        if (casilleroRefIds.length > 0) {
          const { data: shipData } = await supabase
            .from('shipments')
            .select('id, unique_package_id, weight_lb')
            .eq('tenant_id', currentTenantId)
            .in('id', casilleroRefIds);
          for (const s of (shipData || [])) {
            shipmentsMap[s.id] = s;
          }
        }

        // Query 3: encomienda_packages lookup for encomienda packages
        const encomiendaRefIds = (bpData || [])
          .filter(r => r.package_type === 'encomienda')
          .map(r => r.package_ref_id);

        let encomiendaPkgsMap = {};
        if (encomiendaRefIds.length > 0) {
          const { data: encData } = await supabase
            .from('encomienda_packages')
            .select('id, unique_package_id, package_number')
            .eq('tenant_id', currentTenantId)
            .in('id', encomiendaRefIds);
          for (const e of (encData || [])) {
            encomiendaPkgsMap[e.id] = e;
          }
        }

        // Merge package data onto each row under .shipments (preserves downstream shape)
        const bpMap = {};
        for (const row of (bpData || [])) {
          let pkgInfo = null;
          if (row.package_type === 'casillero') {
            pkgInfo = shipmentsMap[row.package_ref_id] || null;
          } else if (row.package_type === 'encomienda') {
            pkgInfo = encomiendaPkgsMap[row.package_ref_id] || null;
          }
          const enriched = { ...row, shipments: pkgInfo };
          if (!bpMap[row.box_id]) bpMap[row.box_id] = [];
          bpMap[row.box_id].push(enriched);
        }
        setBoxPackagesData(bpMap);

        // Query 3: box_client_groups — per-client weight within each box
        const { data: bcgData } = await supabase
          .from('box_client_groups')
          .select('box_id, client_id, weight_lb')
          .eq('tenant_id', currentTenantId)
          .in('box_id', boxIds);
        const bcgMap = {};
        for (const r of (bcgData || [])) {
          bcgMap[`${r.box_id}|${r.client_id}`] = r.weight_lb;
        }
        setBoxClientGroups(bcgMap);

        const uniqueClientIds = [...new Set((bpData || []).map(r => r.client_id).filter(Boolean))];
        if (uniqueClientIds.length > 0) {
          const { data: custData } = await supabase
            .from('customers')
            .select('id, full_name')
            .eq('tenant_id', currentTenantId)
            .in('id', uniqueClientIds);
          const namesObj = {};
          for (const c of (custData || [])) namesObj[c.id] = c.full_name;
          setCustomerNames(namesObj);
        } else {
          setCustomerNames({});
        }

        // Query 5: customer_addresses for secondary clients
        const primaryByBoxIdLocal = {};
        for (const [boxId, rows] of Object.entries(bpMap)) {
          const pr = rows.find(r => r.is_primary);
          if (pr) primaryByBoxIdLocal[boxId] = pr.client_id;
        }
        const secondaryClientIds = [...new Set(
          stopsArr
            .filter(s => s.client_id && primaryByBoxIdLocal[s.box_id] && s.client_id !== primaryByBoxIdLocal[s.box_id])
            .map(s => s.client_id)
            .filter(Boolean)
        )];
        if (secondaryClientIds.length > 0) {
          const { data: addrData } = await supabase
            .from('customer_addresses')
            .select('customer_id, direccion, departamento, municipio, recipient_name, recipient_phone, reference_point, is_default')
            .eq('tenant_id', currentTenantId)
            .in('customer_id', secondaryClientIds);
          const addrMap = {};
          for (const a of (addrData || [])) {
            if (!addrMap[a.customer_id] || a.is_default) addrMap[a.customer_id] = a;
          }
          setCustomerAddresses(addrMap);
        } else {
          setCustomerAddresses({});
        }

        const { data: boxesData } = await fetchBoxRows(currentTenantId, boxIds);
        const { seqs, info } = boxRowsToMaps(boxesData);
        setBoxSequences(seqs);
        setBoxInfo(info);
      } else {
        setBoxPackagesData({});
        setCustomerNames({});
        setBoxClientGroups({});
        setCustomerAddresses({});
        setBoxSequences({});
        setBoxInfo({});
      }
      setStopsLoading(false);
    };

    fetchStops();
  }, [selectedGuiaId, currentTenantId]);

  // ── Realtime — scoped to selected guía ─────────────────────────────────────
  useEffect(() => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    if (!selectedGuiaId) return;

    const channel = supabase
      .channel(`guia-stops-${selectedGuiaId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'guia_stops',
          filter: `guia_id=eq.${selectedGuiaId}`,
        },
        (payload) => {
          setStops(prev => prev.map(s => s.id === payload.new.id ? { ...s, ...payload.new } : s));
        }
      )
      .subscribe();

    channelRef.current = channel;
    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [selectedGuiaId]);

  // ── Update stop status ──────────────────────────────────────────────────────
  const handleUpdateStopStatus = async (stop, newStatus) => {
    setUpdatingStopId(stop.id);
    const { error } = await supabase
      .from('guia_stops')
      .update({ status: newStatus, completed_at: new Date().toISOString() })
      .eq('id', stop.id)
      .eq('tenant_id', currentTenantId);
    if (!error) {
      setStops(prev =>
        prev.map(s => s.id === stop.id ? { ...s, status: newStatus, completed_at: new Date().toISOString() } : s)
      );
    }
    setUpdatingStopId(null);
  };

  // ── Assign driver ───────────────────────────────────────────────────────────
  const handleSaveDriver = async () => {
    if (!selectedGuiaId || !selectedDriverId) return;
    setSavingDriver(true);
    await supabase
      .from('guias')
      .update({ driver_id: selectedDriverId, assigned_by: currentUserId })
      .eq('id', selectedGuiaId)
      .eq('tenant_id', currentTenantId);
    setGuias(prev =>
      prev.map(g => g.id === selectedGuiaId ? { ...g, driver_id: selectedDriverId, assigned_by: currentUserId } : g)
    );
    setSavingDriver(false);
  };

  const toggleSubsection = (box_id, client_id) => {
    const key = `${box_id}|${client_id}`;
    setExpandedSubsections(prev => ({ ...prev, [key]: !prev[key] }));
  };
  const isSubExpanded = (box_id, client_id, isPrimary) => {
    const key = `${box_id}|${client_id}`;
    if (expandedSubsections[key] === undefined) return isPrimary;
    return expandedSubsections[key];
  };

  // ── Summary counts ──────────────────────────────────────────────────────────
  const pendingCount     = stops.filter(s => s.status === 'pending').length;
  const completedCount   = stops.filter(s => s.status === 'delivered' || s.status === 'at_boxful').length;
  const rescheduledCount = stops.filter(s => s.status === 'rescheduled').length;

  // ── Derived render data ─────────────────────────────────────────────────────
  // primary client per box from box_packages
  const primaryByBoxId = {};
  for (const [boxId, rows] of Object.entries(boxPackagesData)) {
    const pr = rows.find(r => r.is_primary);
    if (pr) primaryByBoxId[boxId] = pr.client_id;
  }

  // Sort: by display_sequence ascending, primary stop first within each box group
  const sortedStops = [...stops].sort((a, b) => {
    const seqA = boxSequences[a.box_id] ?? Infinity;
    const seqB = boxSequences[b.box_id] ?? Infinity;
    if (seqA !== seqB) return seqA - seqB;
    const aPri = (primaryByBoxId[a.box_id] === a.client_id) ? 0 : 1;
    const bPri = (primaryByBoxId[b.box_id] === b.client_id) ? 0 : 1;
    return aPri - bPri;
  });

  // Sequence numbers come from DB (display_sequence column on boxes)
  const boxIdToSeq = boxSequences;

  // Client ordering and stop count per box
  const boxClientOrder = {};
  const stopsPerBox = {};
  for (const stop of sortedStops) {
    if (!boxClientOrder[stop.box_id]) boxClientOrder[stop.box_id] = [];
    boxClientOrder[stop.box_id].push(stop.client_id ?? stop.id);
    stopsPerBox[stop.box_id] = (stopsPerBox[stop.box_id] || 0) + 1;
  }

  // Sequence duplicate detection (computed once)
  const seqCounts = {};
  Object.values(boxSequences).forEach(s => { if (s != null) seqCounts[s] = (seqCounts[s] || 0) + 1; });

  // Group stops by box_id preserving sort order
  const groupedByBox = [];
  const seenBoxes = new Set();
  for (const stop of sortedStops) {
    if (!seenBoxes.has(stop.box_id)) {
      seenBoxes.add(stop.box_id);
      groupedByBox.push({
        box_id: stop.box_id,
        stops: sortedStops.filter(s => s.box_id === stop.box_id),
      });
    }
  }

  // ── Loading / empty states ──────────────────────────────────────────────────
  if (loading) return (
    <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
      Cargando guías...
    </div>
  );

  if (guias.length === 0) return (
    <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
      <div style={{ fontSize: '36px', marginBottom: '12px' }}>🗺️</div>
      <p>No hay guías de entrega disponibles.</p>
    </div>
  );

  return (
    <div style={{ maxWidth: '720px', margin: '0 auto', padding: '0 0 80px' }}>

      {/* ── Guía selector ── */}
      <div style={{ marginBottom: '12px' }}>
        <label style={{
          fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)',
          textTransform: 'uppercase', letterSpacing: '0.05em',
          display: 'block', marginBottom: '6px',
        }}>
          Guía de Entrega
        </label>
        <select
          className="input"
          value={selectedGuiaId || ''}
          onChange={e => setSelectedGuiaId(e.target.value || null)}
          style={{ width: '100%' }}
        >
          {guias.map(g => (
            <option key={g.id} value={g.id}>
              Manifiesto {g.manifest_id} — {g.status}
            </option>
          ))}
        </select>
      </div>

      {/* ── Summary bar ── */}
      {selectedGuiaId && (
        <div style={{
          display: 'flex', gap: '16px', padding: '8px 14px', flexWrap: 'wrap',
          background: 'var(--bg)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)', fontSize: '12px', fontWeight: '600',
          marginBottom: '14px',
        }}>
          <span style={{ color: '#6b7280' }}>⏳ {pendingCount} pendientes</span>
          <span style={{ color: '#15803d' }}>✓ {completedCount} completados</span>
          <span style={{ color: '#c2410c' }}>↩ {rescheduledCount} reprogramados</span>
        </div>
      )}

      {/* ── Manager panel — assign driver ── */}
      {isManager && selectedGuiaId && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
          padding: '10px 14px',
          background: 'var(--blue-soft)', border: '1px solid var(--blue)',
          borderRadius: 'var(--radius-sm)', marginBottom: '16px',
        }}>
          <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--blue-text)', whiteSpace: 'nowrap' }}>
            Asignar Conductor
          </span>
          <select
            className="input"
            value={selectedDriverId}
            onChange={e => setSelectedDriverId(e.target.value)}
            style={{ flex: 1, minWidth: '160px' }}
          >
            <option value="">— Sin asignar —</option>
            {drivers.map(d => (
              <option key={d.id} value={d.id}>
                {d.username}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleSaveDriver}
            disabled={savingDriver || !selectedDriverId}
            style={{
              padding: '6px 14px', background: 'var(--blue)', color: '#fff',
              border: 'none', borderRadius: 'var(--radius-sm)',
              fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap',
              cursor: savingDriver || !selectedDriverId ? 'not-allowed' : 'pointer',
            }}
          >
            {savingDriver ? 'Guardando...' : 'Asignar'}
          </button>
        </div>
      )}

      {selectedGuiaId && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {stopsLoading && (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }}>
              Cargando paradas...
            </div>
          )}
          {seqMsg && (
            <div role={seqMsg.tipo === 'error' ? 'alert' : 'status'} style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '8px 12px', borderRadius: 'var(--radius-sm)', fontSize: '12px', fontWeight: '600',
              background: seqMsg.tipo === 'error' ? '#fef2f2' : '#dcfce7',
              color: seqMsg.tipo === 'error' ? '#b91c1c' : '#15803d',
              border: `1px solid ${seqMsg.tipo === 'error' ? '#fecaca' : '#86efac'}`,
            }}>
              <span style={{ flex: 1 }}>{seqMsg.texto}</span>
              <button type="button" onClick={() => setSeqMsg(null)} aria-label="Cerrar"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>✕</button>
            </div>
          )}
          {!stopsLoading && sortedStops.length === 0 && (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
              Sin paradas en esta guía.
            </div>
          )}

          {groupedByBox.map((box) => {
            const sequence = boxIdToSeq[box.box_id] ?? '?';
            const isDuplicateSeq = sequence !== '?' && seqCounts[sequence] > 1;
            const isShared = box.stops.length > 1;
            const firstStop = box.stops[0];
            const isBoxful = firstStop.stop_type === 'boxful';
            const boxMeta = firstStop.boxes || {};
            const municipio = boxMeta.municipio;
            const departamento = boxMeta.departamento;

            // Combined status for outer header
            const anyPending = box.stops.some(s => s.status === 'pending');
            const allCompleted = box.stops.every(s => s.status === 'delivered' || s.status === 'at_boxful');
            let combinedStatusLabel, combinedStatusStyle;
            if (anyPending) {
              combinedStatusLabel = STATUS_LABELS.pending;
              combinedStatusStyle = STATUS_COLORS.pending;
            } else if (allCompleted) {
              combinedStatusLabel = 'Completado';
              combinedStatusStyle = STATUS_COLORS.delivered;
            } else {
              combinedStatusLabel = STATUS_LABELS[firstStop.status] || STATUS_LABELS.pending;
              combinedStatusStyle = STATUS_COLORS[firstStop.status] || STATUS_COLORS.pending;
            }

            const handleSeqSave = (newVal) => saveBoxSequence(box.box_id, newVal);
            const boxCode = boxInfo[box.box_id]?.global_package_id;

            return (
              <div key={box.box_id} style={{
                background: 'var(--tile)',
                border: isShared ? '2px solid #00D4B8' : '1px solid var(--border)',
                borderLeft: isShared ? '3px solid #00D4B8' : '3px solid var(--border)',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: 'var(--tile-shadow)',
                padding: 0,
              }}>
                {/* Outer card header — shared across all stops in this box */}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 14px', borderBottom: '1px solid var(--border)',
                  flexWrap: 'wrap', gap: '6px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    {editingSeqBoxId === box.box_id ? (
                      <input
                        type="number"
                        defaultValue={sequence === '?' ? '' : sequence}
                        autoFocus
                        onBlur={(e) => handleSeqSave(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSeqSave(e.target.value);
                          if (e.key === 'Escape') setEditingSeqBoxId(null);
                        }}
                        style={{ width: '60px', padding: '2px 6px', fontSize: '13px', fontFamily: 'monospace', fontWeight: '700' }}
                      />
                    ) : (
                      <span style={{ fontFamily: 'monospace', fontWeight: '700', color: isDuplicateSeq ? '#dc2626' : 'var(--blue-text)', fontSize: '13px' }}>
                        CAJA N° {sequence}
                        {canEditSeq && (
                          <span
                            onClick={() => setEditingSeqBoxId(box.box_id)}
                            style={{ marginLeft: '6px', cursor: 'pointer', fontSize: '11px', opacity: 0.7 }}
                            title="Editar número"
                          >✎</span>
                        )}
                      </span>
                    )}
                    {boxCode && (
                      <span style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--text-secondary)' }}>
                        {boxCode}
                      </span>
                    )}
                    {isDuplicateSeq && (
                      <span style={{
                        fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '99px',
                        background: '#fef3c7', color: '#92400e',
                      }}>⚠ Número duplicado</span>
                    )}
                    <span style={{
                      fontSize: '10px', fontWeight: '700', padding: '2px 7px', borderRadius: '99px',
                      background: isBoxful ? '#dbeafe' : '#dcfce7',
                      color: isBoxful ? '#1d4ed8' : '#15803d',
                      border: `1px solid ${isBoxful ? '#93c5fd' : '#86efac'}`,
                    }}>{isBoxful ? 'BOXFUL' : 'DROP OFF'}</span>
                    {isShared && (
                      <span style={{
                        fontSize: '10px', fontWeight: '700', padding: '2px 7px', borderRadius: '99px',
                        background: '#00D4B8', color: '#fff',
                      }}>📦 Caja Compartida</span>
                    )}
                    {(municipio || departamento) && (
                      <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                        {municipio}{municipio && departamento ? ', ' : ''}{departamento}
                      </span>
                    )}
                  </div>
                  <span style={{
                    fontSize: '10px', fontWeight: '700', padding: '3px 10px', borderRadius: '99px',
                    background: combinedStatusStyle.bg, color: combinedStatusStyle.color,
                    border: `1px solid ${combinedStatusStyle.border}`,
                  }}>{combinedStatusLabel}</span>
                </div>

                {/* Sub-sections — one per client/stop */}
                {box.stops.map((stop, stopIdx) => {
                  const isPrimary = stop.client_id === primaryByBoxId[box.box_id];
                  const expanded = isSubExpanded(box.box_id, stop.client_id, isPrimary);
                  const isTerminal = ['delivered', 'at_boxful', 'rescheduled'].includes(stop.status);
                  const isUpdating = updatingStopId === stop.id;
                  const statusStyle = STATUS_COLORS[stop.status] || STATUS_COLORS.pending;
                  const statusLabel = STATUS_LABELS[stop.status] || STATUS_LABELS.pending;

                  const allBpRows = boxPackagesData[box.box_id] || [];
                  const fullName = customerNames[stop.client_id] || '';

                  // Address resolution
                  const stopBox = stop.boxes || {};
                  let recipientName, recipientAddress, recipientPhone, refPoint;
                  if (isPrimary) {
                    recipientName    = stopBox.recipient_name;
                    recipientAddress = stopBox.recipient_address;
                    recipientPhone   = stopBox.recipient_phone;
                    refPoint         = stopBox.reference_point;
                  } else {
                    const addr = customerAddresses[stop.client_id];
                    if (addr) {
                      recipientName    = addr.recipient_name;
                      recipientAddress = addr.direccion;
                      recipientPhone   = addr.recipient_phone;
                      refPoint         = addr.reference_point;
                    }
                  }

                  // Packages for THIS client
                  const thisClientPkgs = allBpRows.filter(r => r.client_id === stop.client_id);
                  const pkgLabels = thisClientPkgs
                    .map(r => r.shipments?.unique_package_id
                      ?? (r.shipments?.package_number ? `Paquete ${r.shipments.package_number}` : r.package_ref_id))
                    .join(', ');
                  const groupWeight = boxClientGroups[`${box.box_id}|${stop.client_id}`];

                  return (
                    <div key={stop.id} style={{
                      borderTop: stopIdx === 0 ? 'none' : '1px solid var(--border)',
                      borderLeft: isPrimary ? '3px solid var(--blue)' : '3px solid #d1d5db',
                      marginLeft: isPrimary ? 0 : '12px',
                    }}>
                      {/* Collapsed row — always visible, click to toggle */}
                      <div
                        onClick={() => toggleSubsection(box.box_id, stop.client_id)}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '10px 14px',
                          cursor: 'pointer',
                          userSelect: 'none',
                        }}
                      >
                        <span style={{
                          fontSize: isPrimary ? '13px' : '12px',
                          fontWeight: '600',
                          color: 'var(--text)',
                        }}>
                          {stop.client_id}{fullName ? ` — ${fullName}` : ''} — {thisClientPkgs.length} paquetes
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{
                            fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '99px',
                            background: statusStyle.bg, color: statusStyle.color, border: `1px solid ${statusStyle.border}`,
                          }}>{statusLabel}</span>
                          <span style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1 }}>
                            {expanded ? '▾' : '▸'}
                          </span>
                        </div>
                      </div>

                      {/* Expanded content */}
                      {expanded && (
                        <div style={{ padding: '0 14px 12px 14px' }}>
                          {!recipientName && !recipientAddress ? (
                            <div style={{ fontSize: '12px', color: '#c2410c', fontStyle: 'italic' }}>
                              ⚠ Cliente sin dirección guardada
                            </div>
                          ) : (
                            <>
                              {recipientName && (
                                <div style={{ fontWeight: '700', fontSize: isPrimary ? '14px' : '13px' }}>
                                  {recipientName}
                                </div>
                              )}
                              {recipientAddress && (
                                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                                  📍 {recipientAddress}
                                </div>
                              )}
                              {recipientPhone && (
                                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                                  📞 {recipientPhone}
                                </div>
                              )}
                              {refPoint && (
                                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic', marginTop: '2px' }}>
                                  🔖 {refPoint}
                                </div>
                              )}
                            </>
                          )}
                          <div style={{
                            marginTop: '10px', padding: '8px 10px',
                            background: '#f9fafb', border: '1px solid var(--border)', borderRadius: '6px',
                            fontSize: '12px',
                          }}>
                            <strong>{stop.client_id}</strong>{fullName ? ` — ${fullName}` : ''} — paquetes: {pkgLabels || '—'}
                            {groupWeight != null && ` — Peso: ${groupWeight} lbs`}
                            {' — Total: '}{thisClientPkgs.length}
                          </div>

                          {/* Dispatch action */}
                          {isDispatch && !isTerminal && (
                            <div style={{ marginTop: '12px' }}>
                              {stop.stop_type === 'direct' && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); handleUpdateStopStatus(stop, 'delivered'); }}
                                  disabled={isUpdating}
                                  style={{
                                    padding: '8px 16px', background: '#16a34a', color: '#fff',
                                    border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '700',
                                    cursor: isUpdating ? 'not-allowed' : 'pointer', opacity: isUpdating ? 0.6 : 1,
                                  }}
                                >{isUpdating ? 'Actualizando...' : '✓ Marcar Entregado'}</button>
                              )}
                              {stop.stop_type === 'boxful' && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); handleUpdateStopStatus(stop, 'at_boxful'); }}
                                  disabled={isUpdating}
                                  style={{
                                    padding: '8px 16px', background: '#1d4ed8', color: '#fff',
                                    border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '700',
                                    cursor: isUpdating ? 'not-allowed' : 'pointer', opacity: isUpdating ? 0.6 : 1,
                                  }}
                                >{isUpdating ? 'Actualizando...' : '📦 Entregado a Boxful'}</button>
                              )}
                            </div>
                          )}
                          {isTerminal && (
                            <div style={{ marginTop: '10px', fontSize: '11px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                              ✓ {stop.completed_at ? new Date(stop.completed_at).toLocaleString('es-SV') : 'Completado'}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GuiasPortal;
